import React, {useState, useEffect} from 'react'
import styled from 'styled-components'
import {useAppState} from '../../state'
import {getHighestRoleForUser} from '../../Components/Views/Utils'
import {useWebstoreSso} from '../../hooks/useWebstoreSso'

interface AdBannerProps {
  bannerAssetId?: string | null
  selectedChainId?: string
}

const AdBanner = ({bannerAssetId, selectedChainId}: AdBannerProps) => {
  const {state, actions} = useAppState()
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const {submitSsoRedirect} = useWebstoreSso()

  // Check if user has manager role or is superuser
  const highestRole = state.me?.user ? getHighestRoleForUser(state.me.user) : null
  const isManagerRole = highestRole ? ['CM', 'BM', 'GM', 'LM', 'SM'].includes(highestRole) : false
  const canAccessBanner = Boolean(state.me?.accessRights.superuser || isManagerRole)

  // Check if webstore is enabled
  const selectedChain = selectedChainId ? state.chainsById[selectedChainId] : undefined
  const isWebstoreEnabled = Boolean(selectedChain?.applianceWebstoreEnabled)

  // Determine if banner should be visible
  const shouldShowBanner = Boolean(bannerAssetId && canAccessBanner && isWebstoreEnabled)

  // Fetch image URL
  useEffect(() => {
    if (!shouldShowBanner) return

    const fetchImageUrl = async () => {
      try {
        const downloadUrl = await actions.getDownloadUrl(bannerAssetId!)
        setImageUrl(downloadUrl.downloadURL)
      } catch (error) {
        console.error('Failed to load banner image:', error)
      }
    }

    fetchImageUrl()
  }, [bannerAssetId, shouldShowBanner, actions])

  const handleBannerClick = async () => {
    if (!imageUrl) return
    await submitSsoRedirect()
  }

  if (!shouldShowBanner || !imageUrl) {
    return null
  }

  return (
    <BannerContainer>
      <BannerImage
        src={imageUrl}
        alt="Appliance Webstore"
        onClick={handleBannerClick}
        data-cy="ad-banner"
      />
    </BannerContainer>
  )
}

const BannerContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  width: 100%;
`

const BannerImage = styled.img`
  width: 100%;
  border-radius: 10px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }
`

export default AdBanner
